// Photos, music and video for invitations, uploaded to the Gatherly API.
// Invitations keep the returned ids; files are served from /api/media/<id>.
import { API_BASE, getToken, refreshSession } from "./api";

const MAX_PHOTO_SIDE = 1600;
const MAX_AUDIO_BYTES = 15 * 1024 * 1024;
const MAX_VIDEO_BYTES = 60 * 1024 * 1024;

export const mediaUrl = (id) => (id ? `${API_BASE}/media/${id}` : null);

// Uploads with progress (fetch can't report it), resolving to the new id.
// Errors carry a translation key as their message. An expired sign-in is
// renewed once and the upload sent again.
const upload = (blob, filename, onProgress, retried = false) =>
  new Promise((resolve, reject) => {
    const form = new FormData();
    form.append("file", blob, filename);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}/media`);
    const token = getToken();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (event) => event.lengthComputable && onProgress?.(event.loaded / event.total);
    xhr.onload = async () => {
      let data = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        data = null;
      }
      if (xhr.status >= 200 && xhr.status < 300 && data?.id) return resolve(data.id);
      if (xhr.status === 401 && token && !retried && (await refreshSession(token))) {
        return upload(blob, filename, onProgress, true).then(resolve, reject);
      }
      reject(new Error(data?.error || "media.errors.failed"));
    };
    xhr.onerror = () => reject(new Error("errors.network"));
    xhr.send(form);
  });

// Phone photos are several megabytes; an invitation never needs more than a
// screen's worth of pixels, so they are scaled down before being stored.
const shrink = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const scale = Math.min(1, MAX_PHOTO_SIDE / Math.max(image.width, image.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(image.width * scale);
      canvas.height = Math.round(image.height * scale);
      canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("media.errors.failed"))), "image/jpeg", 0.85);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("media.errors.notImage"));
    };
    image.src = url;
  });

export const savePhoto = async (file, onProgress) => {
  if (!file.type.startsWith("image/")) throw new Error("media.errors.notImage");
  return upload(await shrink(file), "photo.jpg", onProgress);
};

export const saveAudio = async (file, onProgress) => {
  if (!file.type.startsWith("audio/")) throw new Error("media.errors.notAudio");
  if (file.size > MAX_AUDIO_BYTES) throw new Error("media.errors.tooLarge");
  return upload(file, file.name || "audio", onProgress);
};

export const saveVideo = async (file, onProgress) => {
  if (!file.type.startsWith("video/")) throw new Error("media.errors.notVideo");
  if (file.size > MAX_VIDEO_BYTES) throw new Error("media.errors.videoTooLarge");
  return upload(file, file.name || "video", onProgress);
};
